/**
 * Vue Number Format 2.20.0
 * (c) 2021-2023 Dipak Sarkar <hello@dipaksarkar.in> (https://dipaksarkar.in/)
 * @license MIT
 */
import Vue from 'vue';

var options$1 = {
    prefix: '',
    suffix: '',
    separator: ',',
    decimal: '.',
    precision: 2,
    minimumFractionDigits: null,
    prefill: true,
    reverseFill: false,
    min: null,
    max: null,
    nullValue: ''
};

function between(min, n, max) {
    return Math.max(min, Math.min(n, max));
}
// Uncaught RangeError: toFixed() digits argument must be between 0 and 20 at Number.toFixed
function fixed(precision) {
    return between(0, precision, 20);
}
/**
 * Number format class
 * @param {Options} config
 */
class NumberFormat {
    constructor(config) {
        this.options = Object.assign(options$1, config);
        const { prefix, suffix, decimal, reverseFill } = this.options;
        this.input = '';
        this.number = '';
        this.isClean = !reverseFill;
        this.preSurRegExp = new RegExp(`${prefix}|${suffix}`, 'g');
        this.numberRegExp = new RegExp(`[^0-9\\${decimal}]+`, 'gi');
        this.cleanRegExp = new RegExp('[^0-9]+', 'gi');
        this.negativeRegExp = new RegExp('[^0-9\\-]+', 'gi');
        this.isCustomDecimal = decimal !== '.';
    }
    isNull() {
        return !this.numberOnly(this.isClean ? this.cleanRegExp : this.negativeRegExp);
    }
    clean(clean = false) {
        this.isClean = clean;
        return this;
    }
    sign() {
        if (this.input === null || this.input === undefined) {
            return '';
        }
        const hasMinus = this.input.toString().indexOf('-') >= 0;
        if (this.isClean) {
            return hasMinus && this.realNumber() > 0 ? '-' : '';
        }
        return hasMinus ? '-' : '';
    }
    toFixed() {
        const exp = Math.pow(10, this.options.precision);
        const float = parseFloat(this.numberOnly(/\D+/g)) / exp || 0;
        return float.toFixed(fixed(this.options.precision));
    }
    toNumber(str) {
        return Number(str);
    }
    numberOnly(regExp) {
        var _a;
        return (_a = this.input) === null || _a === void 0 ? void 0 : _a.toString().replace(this.preSurRegExp, '').replace(regExp || this.numberRegExp, '');
    }
    isNegative() {
        return this.sign() === '-';
    }
    numbers() {
        const { reverseFill, decimal } = this.options;
        const hasDeciaml = this.input.toString().indexOf(decimal) >= 0;
        const input = !hasDeciaml && this.isCustomDecimal ? this.input + decimal : this.input;
        if (reverseFill) {
            this.number = this.toFixed().replace('.', decimal);
        }
        else if (typeof this.input === 'number') {
            this.number = this.parts(this.input.toString().replace('-', ''), '.').join(decimal);
        }
        else if (!isNaN(this.toNumber(input))) {
            this.number = this.parts(this.input.replace('-', ''), '.').join(decimal);
        }
        else {
            this.number = this.parts(this.numberOnly()).join(decimal);
        }
        return this.number;
    }
    unformatNumber() {
        return this.numbers().toString().replace(this.options.decimal, '.');
    }
    realNumber() {
        return parseFloat(this.unformatNumber());
    }
    parts(num, separator) {
        const { precision, minimumFractionDigits, decimal } = this.options;
        let parts = num.toString().split(separator || decimal);
        if (parts.length > 1) {
            parts[0] = this.toNumber(parts[0]) || 0;
            parts[1] = parts.slice(1, parts.length).join('');
            parts = parts.slice(0, 2);
        }
        if (this.isClean) {
            const newNumber = this.toNumber(parts.join('.')).toFixed(precision);
            const cleanNumber = this.toNumber(newNumber);
            const minimumDigits = cleanNumber.toFixed(minimumFractionDigits);
            const hasMinFraction = minimumFractionDigits >= 0 && cleanNumber.toString().length < minimumDigits.length;
            if (hasMinFraction) {
                parts = minimumDigits.toString().split('.');
            }
            else {
                parts = cleanNumber.toString().split('.');
            }
        }
        return parts.slice(0, 2);
    }
    addSeparator() {
        const { decimal, separator } = this.options;
        const parts = this.numbers().split(decimal);
        parts[0] = parts[0].toString().replace(/(\d)(?=(?:\d{3})+\b)/gm, `$1${separator}`);
        return parts.join(decimal);
    }
    /**
     * Format the input with default config if there is no constructor config
     * @param {Input} input
     * @return {String}
     */
    format(input) {
        this.input = input;
        const { reverseFill, nullValue, prefix, suffix } = this.options;
        if (this.isNull() && !reverseFill) {
            return nullValue;
        }
        return this.sign() + prefix + this.addSeparator() + suffix;
    }
    /**
     * Unformat the input with default config if there is no constructor config
     * @param {Input} input
     * @return {String}
     */
    unformat(input) {
        this.input = input;
        const { reverseFill, nullValue } = this.options;
        if (this.isNull()) {
            return nullValue;
        }
        if (reverseFill && this.realNumber() === 0) {
            return nullValue;
        }
        return this.sign() + this.realNumber();
    }
}

const MINUS = '-';
/**
 * Creates a fuction to clone the objcet
 */
function cloneDeep(data) {
    return JSON.parse(JSON.stringify(data));
}
/**
 * Creates a CustomEvent with detail = { facade: true }
 * used as a way to identify our own event
 */
function InputEvent(event) {
    return new CustomEvent(event, {
        bubbles: true,
        cancelable: true,
        detail: { facade: true }
    });
}
/**
 * ensure that the element we're attaching to is an input element
 * if not try to find an input element in this elements childrens
 */
function getInputElement(el) {
    const inputElement = el instanceof HTMLInputElement ? el : el.querySelector('input');
    /* istanbul ignore next */
    if (!inputElement) {
        throw new Error('facade directive requires an input element');
    }
    return inputElement;
}
/**
 * Updates the cursor position to the right place after the masking rule was applied
 */
function updateCursor(el, position) {
    const setSelectionRange = () => {
        el.setSelectionRange(position, position);
    };
    setSelectionRange();
    // Android Fix
    setTimeout(setSelectionRange, 1);
}
/**
 * Updates the element's value and unmasked value based on the masking config rules
 *
 * @param {CustomInputElement} el The input element to update
 * @param {object} [options]
 * @param {Boolean} options.emit Wether to dispatch a new InputEvent or not
 * @param {Boolean} options.force Forces the update even if the old value and the new value are the same
 */
function updateValue(el, vnode, { emit = true, force = false, clean = false } = {}) {
    const { options, oldValue } = el;
    const { reverseFill, max, min } = options;
    const currentValue = vnode && vnode.data && vnode.data.domProps ? vnode.data.domProps.value : el.value;
    if (force || oldValue !== currentValue) {
        const number = new NumberFormat(options).clean(clean && !reverseFill);
        let masked = number.format(currentValue);
        let unmasked = number.clean(!reverseFill).unformat(currentValue);
        // check value with in range max and min value
        if (clean) {
            if (Number(max) === max && Number(unmasked) > max) {
                masked = number.format(max);
                unmasked = number.unformat(max);
            }
            else if (Number(min) === min && Number(unmasked) < min) {
                masked = number.format(min);
                unmasked = number.unformat(min);
            }
        }
        el.oldValue = masked;
        el.unmasked = unmasked;
        // safari makes the cursor jump to the end if el.value gets assign even if to the same value
        if (el.value !== masked) {
            el.value = masked;
        }
        // this part needs to be outside the above IF statement for vuetify in firefox
        // drawback is that we endup with two's input events in firefox
        return emit && el.dispatchEvent(InputEvent('input'));
    }
}
/**
 * Input event handler
 *
 * @param {CustomInputEvent} event The event object
 */
function inputHandler(event) {
    const { target, detail } = event;
    // We dont need to run this method on the event we emit (prevent event loop)
    if (detail && detail.facade) {
        return false;
    }
    // since we will be emitting our own custom input event
    // we can stop propagation of this native event
    event.stopPropagation();
    let positionFromEnd = target.value.length;
    const { oldValue, options } = target;
    if (target.selectionEnd) {
        positionFromEnd = target.value.length - target.selectionEnd;
    }
    updateValue(target, null, { emit: false, clean: !options.precision });
    // updated cursor position
    if (options.suffix) {
        positionFromEnd = Math.max(positionFromEnd, options.suffix.length);
    }
    positionFromEnd = target.value.length - positionFromEnd;
    if (options.prefix) {
        positionFromEnd = Math.max(positionFromEnd, options.prefix.length);
    }
    updateCursor(target, positionFromEnd);
    if (oldValue !== target.value) {
        target.dispatchEvent(InputEvent('input'));
    }
}
/**
 * Blur event handler
 */
function blurHandler(event) {
    const { target, detail } = event;
    // We dont need to run this method on the event we emit (prevent event loop)
    if (detail && detail.facade) {
        return false;
    }
    const { oldValue } = target;
    updateValue(target, null, { force: true, emit: false, clean: true });
    if (oldValue !== target.value) {
        target.dispatchEvent(InputEvent('input'));
    }
}
/**
 * Keydown event handler
 */
function keydownHandler(event, el) {
    const { options } = el;
    const { prefix, suffix, decimal, min, separator } = options;
    const { key } = event;
    const regExp = new RegExp(`${prefix}|${suffix}`, 'g');
    const newValue = el.value.replace(regExp, '');
    const canNegativeInput = min === undefined || Number(min) < 0 || Number(min) !== min;
    if (key === decimal) {
        if (newValue.includes(decimal)) {
            event.preventDefault();
        }
        else if (!newValue) {
            el.value = '0' + decimal;
            // trigger input event
            el.dispatchEvent(new Event('input'));
        }
    }
    else if (key === MINUS && !canNegativeInput) {
        event.preventDefault();
    }
    else if (key === 'Backspace') {
        // check current cursor position is after separator when backspace key down
        const selectionEnd = el.selectionEnd || 0;
        const character = el.value.slice(selectionEnd - 1, selectionEnd);
        const replace = el.value.slice(selectionEnd - 2, selectionEnd);
        let positionFromEnd = el.value.length - selectionEnd;
        if ([prefix, MINUS, separator].includes(character)) {
            event.preventDefault();
            if (character === separator) {
                el.value = el.value.replace(replace, '');
            }
            else {
                el.value = el.value.replace(new RegExp(`[${prefix}${MINUS}]`, 'g'), '');
            }
            positionFromEnd = Math.max(positionFromEnd, suffix.length);
            positionFromEnd = el.value.length - positionFromEnd;
            positionFromEnd = Math.max(positionFromEnd, prefix.length);
            updateCursor(el, positionFromEnd);
            // trigger input event
            el.dispatchEvent(new Event('input'));
        }
    }
}

var directive = {
    bind: (el, { value, modifiers }, vnode) => {
        el = getInputElement(el);
        const options = Object.assign(cloneDeep(options$1), value, modifiers);
        const { reverseFill, precision, decimal } = options;
        el.options = options;
        if (reverseFill && el.value) {
            el.value = parseFloat(new NumberFormat({ ...options, reverseFill: false }).unformat(el.value)).toFixed(precision);
            if (vnode && vnode.data && vnode.data.domProps) {
                vnode.data.domProps.value = el.value;
            }
        }
        else if (el.value && !isNaN(Number(el.value))) {
            el.value = el.value.replace('.', decimal);
        }
        // set initial value
        updateValue(el, vnode, { force: options.prefill, clean: true, emit: false });
    },
    inserted: (el) => {
        el = getInputElement(el);
        // prefer adding event listener to parent element to avoid Firefox bug which does not
        // execute `useCapture: true` event handlers before non-capturing event handlers
        const handlerOwner = el.parentElement || el;
        // use anonymous event handler to avoid inadvertently removing masking for all inputs within a container
        const oninput = (e) => {
            if (e.target !== el) {
                return;
            }
            inputHandler(e);
        };
        handlerOwner.addEventListener('input', oninput, true);
        el.onblur = (e) => blurHandler(e);
        // check decimal key and insert to current element
        // updated cursor position after format the value
        el.onkeydown = (e) => keydownHandler(e, el);
        el.cleanup = () => handlerOwner.removeEventListener('input', oninput, true);
    },
    update: (el, { value, oldValue, modifiers }, vnode) => {
        el = getInputElement(el);
        const options = el.options;
        el.options = Object.assign(options, value, modifiers);
        if (value !== oldValue) {
            updateValue(el, vnode, { force: true, clean: true });
        }
        else {
            updateValue(el, vnode);
        }
    },
    unbind: (el) => {
        getInputElement(el).cleanup();
    }
};

Vue.directive('number', directive);
const options = cloneDeep(options$1);
var script = Vue.extend({
    name: 'VueNumber',
    props: {
        value: {
            required: true,
            type: [Number, String]
        },
        nullValue: {
            type: [Number, String],
            default: options.nullValue
        },
        masked: Boolean,
        reverseFill: {
            type: Boolean,
            default: options.reverseFill
        },
        prefill: {
            type: Boolean,
            default: options.prefill
        },
        precision: {
            type: Number,
            default: options.precision
        },
        minimumFractionDigits: {
            type: Number,
            default: options.minimumFractionDigits
        },
        decimal: {
            type: String,
            default: options.decimal
        },
        min: {
            type: Number,
            default: options.min
        },
        max: {
            type: Number,
            default: options.max
        },
        separator: {
            type: String,
            default: options.separator
        },
        prefix: {
            type: String,
            default: options.prefix
        },
        suffix: {
            type: String,
            default: options.suffix
        }
    },
    data() {
        return {
            maskedValue: this.value,
            unmaskedValue: this.value
        };
    },
    computed: {
        emittedValue() {
            return this.masked ? this.maskedValue : this.unmaskedValue;
        },
        config() {
            return {
                nullValue: this.nullValue,
                masked: this.masked,
                reverseFill: this.reverseFill,
                prefill: this.prefill,
                precision: this.precision,
                minimumFractionDigits: this.minimumFractionDigits,
                decimal: this.decimal,
                min: this.min,
                max: this.max,
                separator: this.separator,
                prefix: this.prefix,
                suffix: this.suffix
            };
        }
    },
    watch: {
        value(val) {
            if (this.unmaskedValue !== val) {
                this.maskedValue = val;
            }
        }
    },
    methods: {
        input(event) {
            const { target } = event;
            this.maskedValue = target.value;
            this.unmaskedValue = target.unmasked;
            this.$emit('input', this.emittedValue);
        },
        change() {
            this.$emit('change', this.emittedValue);
        }
    }
});

function normalizeComponent(template, style, script, scopeId, isFunctionalTemplate, moduleIdentifier /* server only */, shadowMode, createInjector, createInjectorSSR, createInjectorShadow) {
    if (typeof shadowMode !== 'boolean') {
        createInjectorSSR = createInjector;
        createInjector = shadowMode;
        shadowMode = false;
    }
    // Vue.extend constructor export interop.
    const options = typeof script === 'function' ? script.options : script;
    // render functions
    if (template && template.render) {
        options.render = template.render;
        options.staticRenderFns = template.staticRenderFns;
        options._compiled = true;
        // functional template
        if (isFunctionalTemplate) {
            options.functional = true;
        }
    }
    // scopedId
    if (scopeId) {
        options._scopeId = scopeId;
    }
    let hook;
    if (moduleIdentifier) {
        // server build
        hook = function (context) {
            // 2.3 injection
            context =
                context || // cached call
                    (this.$vnode && this.$vnode.ssrContext) || // stateful
                    (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext); // functional
            // 2.2 with runInNewContext: true
            if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
                context = __VUE_SSR_CONTEXT__;
            }
            // inject component styles
            if (style) {
                style.call(this, createInjectorSSR(context));
            }
            // register component module identifier for async chunk inference
            if (context && context._registeredComponents) {
                context._registeredComponents.add(moduleIdentifier);
            }
        };
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        options._ssrRegister = hook;
    }
    else if (style) {
        hook = shadowMode
            ? function (context) {
                style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot));
            }
            : function (context) {
                style.call(this, createInjector(context));
            };
    }
    if (hook) {
        if (options.functional) {
            // register for functional component in vue file
            const originalRender = options.render;
            options.render = function renderWithStyleInjection(h, context) {
                hook.call(context);
                return originalRender(h, context);
            };
        }
        else {
            // inject component registration as beforeCreate hook
            const existing = options.beforeCreate;
            options.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
    }
    return script;
}

/* script */
const __vue_script__ = script;

/* template */
var __vue_render__ = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("input", {
    directives: [
      {
        name: "number",
        rawName: "v-number",
        value: _vm.config,
        expression: "config",
      },
    ],
    staticClass: "v-number",
    attrs: { type: "text", autocomplete: "off" },
    domProps: { value: _vm.maskedValue },
    on: {
      change: _vm.change,
      input: _vm.input,
      blur: function (evt) {
        return _vm.$emit("blur", evt)
      },
      focus: function (evt) {
        return _vm.$emit("focus", evt)
      },
    },
  })
};
var __vue_staticRenderFns__ = [];
__vue_render__._withStripped = true;

  /* style */
  const __vue_inject_styles__ = undefined;
  /* scoped */
  const __vue_scope_id__ = undefined;
  /* module identifier */
  const __vue_module_identifier__ = undefined;
  /* functional template */
  const __vue_is_functional_template__ = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  );

var index = {
    install(Vue, config) {
        if (config) {
            Object.assign(options$1, config);
        }
        Vue.directive('number', directive);
        Vue.component('VueNumber', __vue_component__);
    }
};

export { NumberFormat, __vue_component__ as component, index as default, directive, options$1 as options };
